import {
  type AuthUser,
  type ConfirmSignInOutput,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  type ResetPasswordOutput,
  resetPassword,
  type SignInOutput,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from '@aws-amplify/auth';
import {
  type UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage } from 'aws-amplify/utils';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { applicationName } from '../constants';
import config from '../config';
import { recordSessionAuditAction } from '../service/form';
import { LastLocation } from '../types';
import { authDebug, getAuthDebugError } from '../utils/auth-debug';
import { getSelectedFormIdFromSession } from '../utils/form-id';
import { getTenantFromPath } from '../utils/tenant-utils';
import { DEFAULT_AUTH_CONFIG, getTenantAuthRealm, TENANT_USER_POOL_CONFIGS } from './userpools';

// Function to configure Amplify for a specific tenant
const configureAmplifyForTenant = (tenant: string) => {
  const tenantConfig = TENANT_USER_POOL_CONFIGS[tenant];

  if (!tenantConfig) {
    Amplify.configure(DEFAULT_AUTH_CONFIG);
    authDebug('amplify.configure.default', {
      tenant,
    });
    return;
  }

  const authConfig: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId: tenantConfig.userPoolId,
        userPoolClientId: tenantConfig.userPoolClientId,
      },
    },
  };

  Amplify.configure(authConfig);
  authDebug('amplify.configure.tenant', {
    tenant,
    userPoolId: tenantConfig.userPoolId,
    userPoolClientId: tenantConfig.userPoolClientId,
  });
};

// Initialize with URL-tenant config to avoid early-session client-id drift.
const initialTenantFromPath =
  typeof window !== 'undefined' ? getTenantFromPath(window.location.pathname) : undefined;
if (initialTenantFromPath && TENANT_USER_POOL_CONFIGS[initialTenantFromPath]) {
  configureAmplifyForTenant(initialTenantFromPath);
} else {
  Amplify.configure(DEFAULT_AUTH_CONFIG);
}

cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);

// Constants for session storage keys
const CURRENT_TENANT_KEY = 'currentTenant';
const ACTIVE_AUTH_SESSION_KEY = 'activeAuthSession';
const TAB_AUTH_SESSION_ID_KEY = 'authTabSessionId';
const SESSION_ROLE_KEY_PREFIX = 'activeSessionRole:';
const SELECTED_FORM_ID_KEY_PREFIX = 'selectedFormId:';

type ActiveAuthSession = {
  sessionId: string;
  tenant?: string;
  userSub?: string;
  userEmail?: string;
  updatedAt: string;
};

type AuthIdentity = {
  sub?: string;
  email?: string;
};

const normalizeEmail = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  return normalized || undefined;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | undefined => {
  const parts = token.split('.');
  if (parts.length < 2) return undefined;
  const payload = parts[1];
  try {
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (_err) {
    return undefined;
  }
};

const getIdentityFromIdToken = (idToken?: string): AuthIdentity => {
  if (!idToken) return {};
  const payload = decodeJwtPayload(idToken);
  if (!payload) return {};
  const sub =
    typeof payload.sub === 'string' && payload.sub.trim() ? payload.sub.trim() : undefined;
  return {
    sub,
    email: normalizeEmail(payload.email),
  };
};

const clearCognitoStorage = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('CognitoIdentityServiceProvider.'))
      .forEach((key) => window.localStorage.removeItem(key));
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith('CognitoIdentityServiceProvider.'))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_err) {}
};

const logCognitoStorageSummary = (tenant?: string) => {
  try {
    const keys = Object.keys(window.localStorage);
    const cognitoKeys = keys.filter((key) => key.startsWith('CognitoIdentityServiceProvider.'));
    const sessionKeys = cognitoKeys.filter((key) =>
      /(idToken|accessToken|refreshToken|LastAuthUser|clockDrift)$/.test(key),
    );
    const suffixes = Array.from(
      new Set(sessionKeys.map((key) => key.split('.').slice(-1)[0])),
    ).sort();
    authDebug('cognito.storage.summary', {
      tenant,
      cognitoKeyCount: cognitoKeys.length,
      sessionKeySuffixes: suffixes,
    });
  } catch (_err) {}
};

// Helper function to get/set tenant from browser localStorage
const getPersistedTenant = (): string | null => {
  return window.localStorage.getItem(CURRENT_TENANT_KEY);
};

const setPersistedTenant = (tenant: string): void => {
  window.localStorage.setItem(CURRENT_TENANT_KEY, tenant);
};

const parseActiveAuthSession = (rawValue: string | null): ActiveAuthSession | undefined => {
  if (!rawValue) return undefined;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ActiveAuthSession>;
    if (!parsed || typeof parsed.sessionId !== 'string' || !parsed.sessionId.trim()) {
      return undefined;
    }
    return {
      sessionId: parsed.sessionId.trim(),
      tenant: typeof parsed.tenant === 'string' ? parsed.tenant : undefined,
      userSub: typeof parsed.userSub === 'string' ? parsed.userSub : undefined,
      userEmail: normalizeEmail(parsed.userEmail),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch (_err) {
    return undefined;
  }
};

const getOrCreateTabSessionId = (): string => {
  try {
    const existing = window.sessionStorage.getItem(TAB_AUTH_SESSION_ID_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(TAB_AUTH_SESSION_ID_KEY, generated);
    return generated;
  } catch (_err) {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

const activateAuthSession = (tenant?: string, identity?: AuthIdentity): void => {
  try {
    const payload: ActiveAuthSession = {
      sessionId: getOrCreateTabSessionId(),
      tenant,
      userSub: identity?.sub,
      userEmail: identity?.email,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(ACTIVE_AUTH_SESSION_KEY, JSON.stringify(payload));
  } catch (_err) {}
};

const clearActiveAuthSessionIfOwned = (): void => {
  try {
    const active = parseActiveAuthSession(window.localStorage.getItem(ACTIVE_AUTH_SESSION_KEY));
    if (!active) return;
    if (active.sessionId !== getOrCreateTabSessionId()) return;
    window.localStorage.removeItem(ACTIVE_AUTH_SESSION_KEY);
  } catch (_err) {}
};

const isOnPublicAuthRoute = (pathname: string) =>
  /^(\/[^/]+)?\/(signin|reset-password|reset-password-email|enter-app-mfa-code|set-new-password)\/?$/.test(
    pathname,
  );

const redirectToSigninWithSessionExpired = (tenant?: string) => {
  const pathname = window.location.pathname || '';
  if (isOnPublicAuthRoute(pathname)) return;
  const base = tenant ? `/${tenant}/${config.urls.signin}` : '/error';
  window.location.assign(`${base}?ncode=2`);
};

const redirectToSigninWithSessionConflict = (tenant?: string) => {
  const base = tenant ? `/${tenant}/${config.urls.signin}` : '/error';
  window.location.assign(`${base}?ncode=3`);
};

const pause = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const clearAuthStorage = (
  tenant?: string,
  options: { clearTenant?: boolean; preserveLastLocation?: boolean } = {},
) => {
  const { clearTenant = true, preserveLastLocation = true } = options;
  const lastLocation = preserveLastLocation ? sessionStorage.getItem(LastLocation) : null;
  clearCognitoStorage();
  try {
    window.localStorage.removeItem(ACTIVE_AUTH_SESSION_KEY);
    Object.keys(window.sessionStorage)
      .filter(
        (key) =>
          key === TAB_AUTH_SESSION_ID_KEY ||
          key === LastLocation ||
          key.startsWith(SESSION_ROLE_KEY_PREFIX) ||
          key.startsWith(SELECTED_FORM_ID_KEY_PREFIX),
      )
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_err) {}
  if (clearTenant) {
    window.localStorage.removeItem(CURRENT_TENANT_KEY);
  } else if (tenant) {
    setPersistedTenant(tenant);
  }
  if (lastLocation) {
    sessionStorage.setItem(LastLocation, lastLocation);
  }
};

// This is the state that is not retrieved from Cognito or calculated
interface AuthState {
  challengeName?: string;
  totpSharedSecret?: string;
  totpSetupUri?: string;
  currentTenant?: string;
}

// This is state mutators, state obtained from useQuery, and calculated state
export interface AuthContextProps extends AuthState {
  user?: AuthUser | null;
  isAuthenticated: boolean;
  signIn: UseMutateFunction<
    SignInOutput,
    Error,
    {
      username: string;
      password: string;
    },
    unknown
  >;
  confirmSignIn: UseMutateFunction<ConfirmSignInOutput, Error, string, unknown>;
  fetchJWT(options?: { redirectOnFail?: boolean }): Promise<{
    accessToken?: string;
    idToken?: string;
  }>;
  fetchMfaSetupDetails(): void;
  signOut: UseMutateFunction<void, Error, void, unknown>;
  setMfaPreferences: UseMutateFunction<void, Error, string, unknown>;
  resetPassword: UseMutateFunction<ResetPasswordOutput, Error, string, unknown>;
  confirmResetPassword: UseMutateFunction<
    void,
    Error,
    { username: string; newPassword: string; confirmationCode: string },
    unknown
  >;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<AuthState>({});
  const location = useLocation();
  const urlTenant = getTenantFromPath(location.pathname || window.location.pathname) || undefined;
  const forcedLogoutInProgress = useRef(false);
  const tenantSwitchInProgress = useRef(false);

  const queryClient = useQueryClient();

  const forceLogoutAndRedirect = async (tenant?: string) => {
    if (forcedLogoutInProgress.current) return;
    forcedLogoutInProgress.current = true;
    try {
      await signOut();
    } catch (_err) {
    } finally {
      queryClient.clear();
      clearAuthStorage(undefined, {
        clearTenant: true,
        preserveLastLocation: false,
      });
      redirectToSigninWithSessionConflict(tenant);
    }
  };

  const recordSessionAudit = async (
    action: 'login' | 'logout',
    tenantOverride?: string,
  ): Promise<void> => {
    try {
      const tenant =
        tenantOverride ||
        state.currentTenant ||
        urlTenant ||
        getTenantFromPath(window.location.pathname);
      if (!tenant) return;
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const selectedFormId = getSelectedFormIdFromSession(tenant);
      const params = selectedFormId ? { form_id: selectedFormId } : undefined;
      await recordSessionAuditAction(idToken, action, params);
    } catch (_err) {
      // Session audit must never block auth flows.
    }
  };

  // Configure Amplify when tenant changes
  useEffect(() => {
    const currentTenant = urlTenant || getTenantFromPath(window.location.pathname);
    if (!currentTenant) return;

    // Get the persisted tenant from sessionStorage
    const persistedTenant = getPersistedTenant();
    const previousTenant = state.currentTenant || persistedTenant;

    if (
      previousTenant &&
      previousTenant !== currentTenant &&
      getTenantAuthRealm(previousTenant) === getTenantAuthRealm(currentTenant)
    ) {
      configureAmplifyForTenant(currentTenant);
      queryClient.clear();
      setState((previousState) => ({ ...previousState, currentTenant }));
      setPersistedTenant(currentTenant);
      return;
    }

    // Force logout whenever the tenant in URL changes from the previously active tenant.
    if (previousTenant && previousTenant !== currentTenant) {
      if (tenantSwitchInProgress.current) return;
      tenantSwitchInProgress.current = true;
      const switchTenant = async () => {
        try {
          try {
            configureAmplifyForTenant(previousTenant);
            await signOut();
          } catch (_error) {}
          clearActiveAuthSessionIfOwned();
          queryClient.clear();
          clearAuthStorage(undefined, {
            clearTenant: true,
            preserveLastLocation: false,
          });

          configureAmplifyForTenant(currentTenant);
          setState({ currentTenant });
          setPersistedTenant(currentTenant);

          let signinUrl = `/${currentTenant}/${config.urls.signin}?ncode=4`;
          const guideQuery = new URLSearchParams(window.location.search);
          if (
            currentTenant === 'psfa' &&
            (window.location.pathname === '/psfa/guide' || guideQuery.get('returnTo') === '/psfa/guide')
          ) {
            signinUrl += '&returnTo=%2Fpsfa%2Fguide';
          }
          if (
            window.location.pathname !== `/${currentTenant}/${config.urls.signin}` ||
            !window.location.search.includes('ncode=4')
          ) {
            window.location.assign(signinUrl);
          }
        } finally {
          tenantSwitchInProgress.current = false;
        }
      };

      void switchTenant();
    } else if (!persistedTenant) {
      // Initial setup - just configure Amplify without clearing session
      configureAmplifyForTenant(currentTenant);
      setState((prev) => ({
        ...prev,
        currentTenant,
      }));
      setPersistedTenant(currentTenant);
    } else if (persistedTenant && currentTenant === persistedTenant && !state.currentTenant) {
      // Page reload - restore the tenant from sessionStorage
      configureAmplifyForTenant(currentTenant);
      setState((prev) => ({
        ...prev,
        currentTenant,
      }));
      logCognitoStorageSummary(currentTenant);
    }
  }, [location.pathname, queryClient, state.currentTenant, urlTenant]);

  const { isError: authError, data: authUser } = useQuery<AuthUser | null, Error>({
    queryKey: ['cognitoUser', state.currentTenant],
    queryFn: async () => {
      try {
        const user = await getCurrentUser();
        return user;
      } catch (error) {
        if (error instanceof Error && error.name === 'UserUnAuthenticatedException') {
          return null;
        }
        throw error;
      }
    },
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!state.currentTenant,
  });

  const { mutate: setMfaPreferencesMutate } = useMutation<void, Error, string>({
    mutationFn: async (code) => {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ sms: 'DISABLED', totp: 'PREFERRED' });
    },
    onSuccess: () => {
      setState((prev) => ({
        ...prev,
        challengeName: 'DONE',
      }));
    },
  });

  const { mutate: signInMutate } = useMutation<
    SignInOutput,
    Error,
    { username: string; password: string }
  >({
    mutationFn: async ({ username, password }) => {
      const activeTenant =
        state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname);
      authDebug('cognito.signin.start', {
        tenant: activeTenant,
        username,
      });
      try {
        await signOut();
      } catch (error) {
        authDebug('cognito.signout.beforeSignIn.ignored', {
          tenant: activeTenant,
          error: getAuthDebugError(error),
        });
        // ignore if no user is signed in
      }
      return await signIn({ username, password });
    },
    onSuccess: async (output) => {
      queryClient.invalidateQueries({
        queryKey: ['cognitoUser', state.currentTenant],
      });
      sessionStorage.removeItem(LastLocation);
      const activeTenant =
        state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname);
      authDebug('cognito.signin.success', {
        tenant: activeTenant,
        isSignedIn: output.isSignedIn,
        nextStep: output.nextStep?.signInStep,
      });
      if (activeTenant) {
        setPersistedTenant(activeTenant);
        setState((prev) => ({
          ...prev,
          currentTenant: activeTenant,
        }));
      }
      if (output.isSignedIn || output.nextStep?.signInStep === 'DONE') {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        authDebug('cognito.session.afterSignIn', {
          tenant: activeTenant,
          hasIdToken: Boolean(idToken),
          hasAccessToken: Boolean(session.tokens?.accessToken),
        });
        const identity = getIdentityFromIdToken(idToken);
        activateAuthSession(activeTenant || undefined, identity);
        void recordSessionAudit('login', activeTenant || undefined);
      }
      if (output.nextStep) {
        const nextChallenge = output.nextStep.signInStep;

        if (
          output.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' &&
          output.nextStep.totpSetupDetails
        ) {
          const { sharedSecret, getSetupUri } = output.nextStep.totpSetupDetails;
          const setupUri = await getSetupUri(applicationName);
          setState((prev) => ({
            ...prev,
            totpSharedSecret: sharedSecret,
            totpSetupUri: setupUri.toString(),
          }));
        }

        setState((prev) => ({
          ...prev,
          challengeName: nextChallenge,
        }));
      }
    },
    onError: (error) => {
      authDebug('cognito.signin.error', {
        tenant: state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname),
        error: getAuthDebugError(error),
      });
      clearAuthStorage(state.currentTenant);
    },
  });

  const { mutate: confirmSignInMutate } = useMutation<ConfirmSignInOutput, Error, string>({
    mutationFn: async (challengeResponse) => {
      authDebug('cognito.confirmSignIn.start', {
        tenant: state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname),
      });
      return await confirmSignIn({ challengeResponse });
    },
    onSuccess: async (output) => {
      queryClient.invalidateQueries({
        queryKey: ['cognitoUser', state.currentTenant],
      });
      const activeTenant =
        state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname);
      authDebug('cognito.confirmSignIn.success', {
        tenant: activeTenant,
        isSignedIn: output.isSignedIn,
        nextStep: output.nextStep?.signInStep,
      });
      if (activeTenant) {
        setPersistedTenant(activeTenant);
        setState((prev) => ({
          ...prev,
          currentTenant: activeTenant,
        }));
      }
      if (output.isSignedIn || output.nextStep?.signInStep === 'DONE') {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        authDebug('cognito.session.afterConfirmSignIn', {
          tenant: activeTenant,
          hasIdToken: Boolean(idToken),
          hasAccessToken: Boolean(session.tokens?.accessToken),
        });
        const identity = getIdentityFromIdToken(idToken);
        activateAuthSession(activeTenant || undefined, identity);
        void recordSessionAudit('login', activeTenant || undefined);
      }
      if (output.nextStep) {
        const nextChallenge = output.nextStep.signInStep;

        if (
          output.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' &&
          output.nextStep.totpSetupDetails
        ) {
          const { sharedSecret, getSetupUri } = output.nextStep.totpSetupDetails;
          const setupUri = await getSetupUri(applicationName);
          setState((prev) => ({
            ...prev,
            totpSharedSecret: sharedSecret,
            totpSetupUri: setupUri.toString(),
          }));
        }

        setState((prev) => ({
          ...prev,
          challengeName: nextChallenge,
        }));
      }
    },
    onError: (error) => {
      authDebug('cognito.confirmSignIn.error', {
        tenant: state.currentTenant || urlTenant || getTenantFromPath(window.location.pathname),
        error: getAuthDebugError(error),
      });
    },
  });

  const { mutate: signOutMutate } = useMutation<void, Error, void>({
    mutationFn: async () => {
      await recordSessionAudit('logout');
      await signOut();
    },
    onSettled: () => {
      clearActiveAuthSessionIfOwned();
      queryClient.clear();
      clearAuthStorage(undefined, {
        clearTenant: true,
        preserveLastLocation: false,
      });
    },
  });

  const { mutate: resetPasswordMutate } = useMutation<ResetPasswordOutput, Error, string>({
    mutationFn: async (username) => await resetPassword({ username }),
  });

  const { mutate: confirmResetPasswordMutate } = useMutation<
    void,
    Error,
    { username: string; newPassword: string; confirmationCode: string }
  >({
    mutationFn: async ({ username, newPassword, confirmationCode }) =>
      await confirmResetPassword({ username, newPassword, confirmationCode }),
  });

  const isAuthenticated = useMemo(() => {
    const { challengeName } = state;
    return (
      !authError && authUser != null && (challengeName === 'DONE' || challengeName === undefined)
    );
  }, [authUser, state, authError]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== ACTIVE_AUTH_SESSION_KEY || !event.newValue) return;
      if (forcedLogoutInProgress.current) return;

      const nextSession = parseActiveAuthSession(event.newValue);
      if (!nextSession) return;

      const tabSessionId = getOrCreateTabSessionId();
      if (nextSession.sessionId === tabSessionId) return;

      const tenantFromPath = getTenantFromPath(window.location.pathname);
      const redirectTenant =
        tenantFromPath || state.currentTenant || urlTenant || nextSession.tenant;
      void forceLogoutAndRedirect(redirectTenant || undefined);
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [forceLogoutAndRedirect, isAuthenticated, state.currentTenant, urlTenant]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const verifySessionIdentity = async () => {
      if (forcedLogoutInProgress.current || cancelled) return;
      const tenantFromPath = getTenantFromPath(window.location.pathname);
      const redirectTenant = state.currentTenant || urlTenant || tenantFromPath || undefined;

      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const identity = getIdentityFromIdToken(idToken);
        if (!identity.sub && !identity.email) return;

        const active = parseActiveAuthSession(window.localStorage.getItem(ACTIVE_AUTH_SESSION_KEY));
        if (
          active &&
          !window.sessionStorage.getItem(TAB_AUTH_SESSION_ID_KEY) &&
          active.tenant === redirectTenant &&
          active.userSub &&
          active.userSub === identity.sub
        ) {
          window.sessionStorage.setItem(TAB_AUTH_SESSION_ID_KEY, active.sessionId);
        }
        const tabSessionId = getOrCreateTabSessionId();

        if (!active || active.sessionId !== tabSessionId) {
          activateAuthSession(redirectTenant, identity);
          return;
        }

        const hasSubMismatch = Boolean(
          active.userSub && identity.sub && active.userSub !== identity.sub,
        );
        const hasEmailMismatch = Boolean(
          !active.userSub &&
            active.userEmail &&
            identity.email &&
            active.userEmail !== identity.email,
        );

        if (hasSubMismatch || hasEmailMismatch) {
          void forceLogoutAndRedirect(redirectTenant);
          return;
        }

        // Keep old sessions compatible: add identity details once if missing.
        if ((!active.userSub && identity.sub) || (!active.userEmail && identity.email)) {
          activateAuthSession(redirectTenant, {
            sub: active.userSub || identity.sub,
            email: active.userEmail || identity.email,
          });
        }
      } catch (_err) {
        // best-effort guard only
      }
    };

    void verifySessionIdentity();

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void verifySessionIdentity();
    };
    const onFocus = () => {
      void verifySessionIdentity();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [forceLogoutAndRedirect, isAuthenticated, state.currentTenant, urlTenant]);

  const fetchJWT = async (
    options: { redirectOnFail?: boolean } = {},
  ): Promise<{
    accessToken?: string;
    idToken?: string;
  }> => {
    const { redirectOnFail = true } = options;
    const tenantFromPath = getTenantFromPath(window.location.pathname);
    const redirectTenant = state.currentTenant || urlTenant || tenantFromPath;

    authDebug('cognito.fetchJWT.start', {
      tenant: redirectTenant,
      redirectOnFail,
    });

    const readSessionTokens = async (): Promise<{
      accessToken?: string;
      idToken?: string;
    }> => {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        return { accessToken, idToken };
      }
      return {};
    };

    try {
      const tokens = await readSessionTokens();
      if (tokens.idToken) {
        authDebug('cognito.fetchJWT.success', {
          tenant: redirectTenant,
          attempt: 'initial',
          hasIdToken: true,
          hasAccessToken: Boolean(tokens.accessToken),
        });
        return tokens;
      }
    } catch (error) {
      authDebug('cognito.fetchJWT.error', {
        tenant: redirectTenant,
        attempt: 'initial',
        error: getAuthDebugError(error),
      });
      // Retry once. Immediately after login, Amplify session tokens may not be hydrated yet.
    }

    await pause(150);

    try {
      const tokens = await readSessionTokens();
      if (tokens.idToken) {
        authDebug('cognito.fetchJWT.success', {
          tenant: redirectTenant,
          attempt: 'retry',
          hasIdToken: true,
          hasAccessToken: Boolean(tokens.accessToken),
        });
        return tokens;
      }
    } catch (error) {
      authDebug('cognito.fetchJWT.error', {
        tenant: redirectTenant,
        attempt: 'retry',
        error: getAuthDebugError(error),
      });
      // fall through to redirect/empty response
    }

    authDebug('cognito.fetchJWT.empty', {
      tenant: redirectTenant,
      redirectOnFail,
    });
    if (redirectOnFail) {
      redirectToSigninWithSessionExpired(redirectTenant || undefined);
    }
    return {};
  };

  // Gets the URI to use for setting up the TOTP MFA
  const fetchMfaSetupDetails = async () => {
    const setupDetails = await setUpTOTP();
    setState((prev) => ({
      ...prev,
      totpSharedSecret: setupDetails.sharedSecret,
      totpSetupUri: setupDetails.getSetupUri(applicationName).toString(),
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        isAuthenticated,
        user: authUser,
        signIn: signInMutate,
        confirmSignIn: confirmSignInMutate,
        fetchJWT,
        fetchMfaSetupDetails,
        signOut: signOutMutate,
        setMfaPreferences: setMfaPreferencesMutate,
        resetPassword: resetPasswordMutate,
        confirmResetPassword: confirmResetPasswordMutate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextProps => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw Error('No AuthProvider found.');
  }
  return authContext;
};
